import React, { forwardRef, useRef } from "react";
import DatePicker from "react-datepicker";
import { mediaMaxWidth } from "./utils/mediaQueriesTemplate";
import styled from "styled-components";
import "react-datepicker/dist/react-datepicker.css";

/* eslint-disable-next-line react/display-name */
const DatePickerCustomInput = forwardRef(
  (
    {
      value,
      onClick,
      placeholder,
      disabled,
    }: {
      value: string | null;
      onClick: () => void;
      placeholder: string;
      disabled: boolean;
    },
    ref
  ) => (
    <InterviewDateOrTimeButton
      type="button"
      className="react-datepicker__custom-input"
      onClick={onClick}
      ref={ref as React.MutableRefObject<HTMLButtonElement>}
      disabled={disabled}
    >
      {value || placeholder}
    </InterviewDateOrTimeButton>
  )
);

const timeIntervals = 15;

const endDateMinTime = (actionAt) => {
  const newDate = new Date(actionAt);
  newDate.setMinutes(newDate.getMinutes() + 5);
  return newDate;
};

const endDateMaxTime = (actionAt) => {
  const newDate = new Date(actionAt);
  newDate.setHours(23);
  newDate.setMinutes(60 - timeIntervals);
  return newDate;
};

const durationElementClassName =
  "react-datepicker__time-list-item-duration-label";

const durationLabelText = (
  endTimeHours: number,
  endTimeMinutes: number,
  startDate: Date
) => {
  const endDate = new Date(startDate);
  endDate.setMinutes(endTimeMinutes);
  endDate.setHours(endTimeHours);

  const difference = Math.abs(endDate.valueOf() - startDate.valueOf()) / 1000;
  const hoursDifference = Math.floor((difference / 3600) % 24);
  const minutesDifference = Math.floor((difference / 60) % 60);

  const hoursLabel = hoursDifference === 1 ? "hr" : "hrs";
  const minsLabel = "mins";

  const hours = `${
    hoursDifference === 0 ? "" : `${hoursDifference} ${hoursLabel}`
  }`;
  const minutes = `${
    minutesDifference === 0
      ? ""
      : `${hoursDifference === 0 ? "" : " "}${minutesDifference} ${minsLabel}`
  }`;

  return ` (${hours}${minutes})`;
};

// This is a bit hacky: on our interview end time
// we want to show the duration of the interview as
// part of the time label e.g. 15:00 (1 hr 30 mins)
// react-datepicker doesn't provide a way to
// customise these labels other than the time format,
// so we use this to inject our own duration
// There is no need for any cleanup as when the calendar
// 'closes' these nodes are removed from the DOM automatically,
// so we can just call this method to add them every time the calendar opens.
const addDurationLabels = (
  endTimeDatePickerWrapperNode: HTMLElement | null,
  startDateTime: Date | null
) => {
  if (endTimeDatePickerWrapperNode === null || startDateTime === null) return;

  const listItems = endTimeDatePickerWrapperNode.querySelectorAll(
    ".react-datepicker__time-list-item:not(.react-datepicker__time-list-item--disabled)"
  );

  listItems.forEach((listItem) => {
    const [hours, minutes] = listItem.textContent
      .split(":")
      .map((timeItem) => Number(timeItem));

    const durationLabel = document.createElement("span");
    durationLabel.textContent = durationLabelText(
      hours,
      minutes,
      startDateTime
    );

    durationLabel.classList.add(durationElementClassName);
    listItem.appendChild(durationLabel);
  });
};

type InterviewDatePickerProps = {
  actionDate: Date | null;
  handleDateChange: (date: Date) => void;
  actionAt: Date | null;
  handleStartTimeChange: (date: Date) => void;
  actionEndAt: Date | null;
  handleEndTimeChange: (date: Date) => void;
};

const InterviewDatePicker: React.FC<InterviewDatePickerProps> = ({
  actionDate,
  handleDateChange,
  actionAt,
  handleStartTimeChange,
  actionEndAt,
  handleEndTimeChange,
}) => {
  const endTimeDatePickerWrapper = useRef(null);

  return (
    <DatePickerRow>
      <DatePickerWrapper className="date-input">
        <DatePicker
          selected={actionDate}
          dateFormat="MMM d, yyyy"
          timeCaption="Start Time"
          minDate={new Date()}
          placeholderText="Add interview date"
          customInput={React.createElement(DatePickerCustomInput)}
          onChange={handleDateChange}
        />
      </DatePickerWrapper>
      <DatePickerWrapper className="time-input">
        <DatePicker
          selected={actionAt}
          showTimeSelect
          showTimeSelectOnly
          timeFormat="HH:mm"
          timeIntervals={timeIntervals}
          dateFormat="HH:mm"
          timeCaption="Start time"
          placeholderText="Start time"
          disabled={actionDate === null}
          customInput={React.createElement(DatePickerCustomInput)}
          onChange={handleStartTimeChange}
        />
      </DatePickerWrapper>
      <InterviewTimeToSpan>to</InterviewTimeToSpan>
      <DatePickerWrapper className="time-input" ref={endTimeDatePickerWrapper}>
        <DatePicker
          selected={actionEndAt}
          showTimeSelect
          showTimeSelectOnly
          timeFormat="HH:mm"
          timeIntervals={timeIntervals}
          dateFormat="HH:mm"
          timeCaption="End time"
          placeholderText="End time"
          openToDate={new Date(actionAt?.getTime() + timeIntervals * 60000)}
          disabled={actionDate === null || actionAt === null}
          customInput={React.createElement(DatePickerCustomInput)}
          minTime={endDateMinTime(actionAt)}
          maxTime={endDateMaxTime(actionAt)}
          onChange={handleEndTimeChange}
          onCalendarOpen={() => {
            addDurationLabels(endTimeDatePickerWrapper.current, actionAt);
          }}
        />
      </DatePickerWrapper>
    </DatePickerRow>
  );
};

const DatePickerRow = styled.div`
  display: flex;
  align-items: center;
  margin: 0.25rem 0;
`;

const DatePickerWrapper = styled.div`
  ${mediaMaxWidth.phone`
    top: 5px;
  `}

  &.date-input .react-datepicker__custom-input {
    width: 150px;
    margin-right: 0.5rem;
    ${mediaMaxWidth.phone`
      width: auto;
    `}
  }

  &.time-input .react-datepicker__custom-input {
    width: 83px;
    ${mediaMaxWidth.phone`
      width: auto;
    `}
  }

  .react-datepicker-wrapper {
    display: flex;
  }

  .react-datepicker__input-container {
    display: flex;
    font-size: 0.92em;
    position: relative;

    ${mediaMaxWidth.phone`
      font-size: 0.8em;
    `}

    .react-datepicker__time-input {
      width: 150px;
    }

    .react-datepicker__date-input {
      width: 92px;
    }
  }

  .react-datepicker__navigation.react-datepicker__navigation--next,
  .react-datepicker__navigation.react-datepicker__navigation--previous {
    height: 30px;
    width: 30px;
    border-radius: 50%;
    background-color: #f2008c;
    background-image: url("/arrow.png");
    background-size: 25px;
    background-repeat: no-repeat;
    background-position: 2px 3px;
    border: none;

    ${mediaMaxWidth.phone`
      height: 20px;
      width: 20px;
      background-size: 15px;
    `}
  }

  .react-datepicker__navigation.react-datepicker__navigation--previous {
    transform: rotate(180deg);
    top: 5px;
    left: 5px;
  }

  .react-datepicker__navigation.react-datepicker__navigation--next {
    top: 5px;
    right: 5px;
  }

  .react-datepicker__navigation-icon {
    display: none;
  }

  .react-datepicker-popper {
    width: 270px;
    cursor: pointer;

    .react-datepicker {
      display: flex;
      flex: 1;
      flex-direction: column;
      border-radius: 4px;
      border: none;
      box-shadow: 0 0 10px #d2d2d2;
    }

    .react-datepicker__header {
      background-color: #fff;
      border-bottom: 1px solid #d2d2d2;
      margin: 5px 20px 0 20px;

      ${mediaMaxWidth.phone`
        padding-top: 0;
      `}
    }

    .react-datepicker__header.react-datepicker__header--time {
      border-radius: 0;
    }

    .react-datepicker-time__header,
    .react-datepicker__current-month {
      color: #333;

      ${mediaMaxWidth.phone`
        font-size: 1em;
      `}
    }

    .react-datepicker__day-names .react-datepicker__day-name {
      color: #333;
    }

    .react-datepicker__day {
      border: 1px solid #fff;
      width: 30px;
      height: 30px;

      &:hover {
        border-radius: 50%;
        border: 1px solid #f2008c;
        background-color: #fff;
      }
    }

    .react-datepicker__day--keyboard-selected,
    .react-datepicker__day.react-datepicker__day--selected.react-datepicker__day--today {
      background-color: #673490;
      border: 1px solid #673490;
      border-radius: 50%;
    }

    .react-datepicker__day.react-datepicker__day--selected {
      background-color: #f2008c;
      border: 1px solid #f2008c;
      border-radius: 50%;
    }

    .react-datepicker__time-container {
      width: 100%;
      border-left: none;
    }

    .react-datepicker__time-box {
      width: 100% !important;
      border-radius: 4px;
    }

    .react-datepicker__time {
      border-radius: 8px;
    }

    .react-datepicker__time-list {
      height: 100px !important;
    }

    .react-datepicker__time-list-item {
      &:hover {
        background-color: #efefef !important;
      }
    }

    .react-datepicker__time-list-item.react-datepicker__time-list-item--selected {
      background-color: #f2008c !important;
    }
  }
`;

const InterviewDateOrTimeButton = styled.button`
  display: block;
  border: 1px solid;
  padding: 4px 5px;
  border-color: ${(props) => props.disabled && "#e4e5e7"};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")} !important;
  font-weight: bold;
  background: none;
  border-radius: 4px;
  background: ${(props) => props.disabled && "#e4e5e7"};
`;

const InterviewTimeToSpan = styled.span`
  margin: 0 0.5rem;
`;

export default InterviewDatePicker;
